import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, poundsFromPence } from "../lib/api";
import { PublicShell } from "./PublicLandingPage";
import { BookingHeader, SummaryPanel } from "../components/BookingChrome";
import { getTemplate, mergeBranding } from "../lib/templates";
import { Plus, Minus, CheckCircle } from "lucide-react";

const PANEL_HEADINGS = { default: "Your Visit", carnival: "Your Booking", haunt: "Your Visit", asylum: "Admission Record", crypt: "Your Visit" };

// Editor live-preview for the Tickets / Checkout / Confirmation tabs (read-only, mock data).
export default function FlowStepPreview({ step }) {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [liveBranding, setLiveBranding] = useState(null);
  const gotRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const list = await api.get(`/public/attraction/${slug}`);
        const es = (list.data.events || [])[0]?.slug;
        if (!es) { setErr("No events available."); return; }
        const ev = await api.get(`/public/attraction/${slug}/event/${es}`);
        const dates = ev.data.dates || [];
        let picked = null;
        for (const d of dates) {
          const det = await api.get(`/public/attraction/${slug}/date/${d.date_id}`);
          if ((det.data.ticket_types || []).length > 0) { picked = det.data; break; }
          if (!picked) picked = det.data;
        }
        setData(picked || { attraction: ev.data.attraction, event: ev.data.event, date: dates[0], ticket_types: [], addons: [] });
        if (!gotRef.current) setLiveBranding((cur) => cur ?? (ev.data.attraction.branding || {}));
      } catch { setErr("Preview unavailable."); }
    })();
  }, [slug]);

  useEffect(() => {
    const handler = (e) => { if (e?.data?.type === "BRANDING_UPDATE" && e.data.branding) { gotRef.current = true; setLiveBranding(e.data.branding); } };
    window.addEventListener("message", handler);
    try { window.parent?.postMessage({ type: "PREVIEW_READY" }, "*"); } catch (_) {}
    return () => window.removeEventListener("message", handler);
  }, []);

  if (err) return <PublicShell><div className="text-center py-32 text-[#fca5a5]">{err}</div></PublicShell>;
  if (!data) return <PublicShell><div className="text-center py-32 text-[#8a8a8a]">Loading…</div></PublicShell>;

  const a = data.attraction;
  const b = mergeBranding(liveBranding || a.branding);
  const aShell = { ...a, branding: b };
  const tplId = getTemplate(b.template_id || "default").id;
  const heading = b.summary_heading || PANEL_HEADINGS[tplId] || "Your Visit";

  if (step === "confirmation") {
    const heading2 = b.confirmation_heading || "Booking Confirmed!";
    const sub = b.confirmation_subheading || "Your tickets are on their way to you.";
    const orderHeading = b.confirmation_order_heading || "Order Summary";
    const ticketHeading = b.confirmation_ticket_heading || "Your Tickets";
    const mockTt = (data.ticket_types || [])[0];
    const ttName = mockTt?.name || "General Admission";
    const ttPrice = mockTt ? (mockTt.effective_price_pence ?? mockTt.base_price_pence) : 2500;
    return (
      <PublicShell attraction={aShell} hideLogo>
        <BookingHeader attraction={a} branding={b} />
        <div className="public-themed max-w-xl mx-auto px-2 py-6" data-testid="confirmation-preview">
          <div className="text-center">
            <div className="inline-flex w-16 h-16 rounded-full items-center justify-center" style={{ background: "var(--accent-color,#dc2626)" }}>
              <CheckCircle size={36} />
            </div>
            <h1 className="confirmation-heading mt-4">{heading2}</h1>
            <p className="confirmation-subheading mt-2 text-sm">{sub}</p>
          </div>
          <div className="confirmation-order-box mt-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm uppercase tracking-[0.18em] opacity-70">{orderHeading}</h3>
              <span className="st-mono text-sm" style={{ color: "var(--accent-color,#dc2626)" }}>#ST-00042</span>
            </div>
            <div className="text-sm space-y-1.5">
              <div className="flex justify-between"><span className="opacity-70">Visit date</span><span>{data.date?.date || "—"}</span></div>
              <div className="flex justify-between"><span className="opacity-70">2× {ttName}</span><span>{poundsFromPence(ttPrice * 2)}</span></div>
            </div>
            <div className="h-px bg-white/10 my-3" />
            <div className="flex justify-between font-bold"><span>Total paid</span><span>{poundsFromPence(ttPrice * 2)}</span></div>
          </div>
          <div className="confirmation-ticket-box mt-4">
            <h3 className="text-sm uppercase tracking-[0.18em] opacity-70 mb-3">{ticketHeading} (2)</h3>
            <div className="space-y-2">
              {["Alex Carter", "Jordan Lee"].map((nm) => (
                <div key={nm} className="flex items-center justify-between bg-black/20 border border-white/10 rounded-lg p-3">
                  <div><div className="font-semibold text-sm">{nm}</div><div className="text-xs opacity-60">{ttName}</div></div>
                  <span className="st-link text-sm">Open ticket →</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell attraction={aShell} hideLogo>
      <BookingHeader attraction={a} branding={b} />
      <div className="booking-body" data-testid="booking-body">
        <div className="flow-box">
          {step === "tickets" ? (
            <>
              <h2 className="pc-heading">{b.ticket_container_heading || "Choose your tickets"}</h2>
              <div className="space-y-2 mt-3" data-testid="tickets-preview">
                {(data.ticket_types || []).map((t) => (
                  <div key={t.id} className="ticket-item">
                    <div>
                      <div className="ticket-name">{t.name}</div>
                      <div className="text-sm ticket-price">{poundsFromPence(t.effective_price_pence ?? t.base_price_pence)}</div>
                    </div>
                    <div className="flex items-center gap-3 opacity-60">
                      <span className="w-8 h-8 rounded-full border border-[#2b2b2b] flex items-center justify-center"><Minus size={14} /></span>
                      <span className="w-6 text-center font-bold">0</span>
                      <span className="w-8 h-8 rounded-full border border-[#2b2b2b] flex items-center justify-center"><Plus size={14} /></span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <h2 className="pc-heading">Your details</h2>
              <div className="grid md:grid-cols-2 gap-3 mt-3 opacity-70" data-testid="checkout-preview">
                <input className="st-input" placeholder="First name" disabled />
                <input className="st-input" placeholder="Last name" disabled />
                <input className="st-input" placeholder="Email" disabled />
                <input className="st-input" placeholder="Phone" disabled />
                <input className="st-input" placeholder="Postcode" disabled />
              </div>
              <button className="info-cta mt-5" style={{ width: "100%" }} disabled data-testid="checkout-pay-preview">Pay now</button>
            </>
          )}
        </div>
        <SummaryPanel heading={heading} dateText={data.date?.date || "—"} showTime={!!data.event?.is_time_slotted}
                      timeText="—" ticketsText="—" totalText="—" />
      </div>
    </PublicShell>
  );
}
